<script lang="ts" setup generic="O = any, V = any">
import { computed, type VNode, provide } from 'vue';
import { useOptionsList, type OptionsListItem } from '../../composables';
import type { OptionsListProps } from '../../shared/types';
import UiSegmentedControlOption from './ui-segmented-control-option.vue';
import { SEGMENTED_CONTROL_CONTEXT } from './shared';
type Props = Omit<OptionsListProps<O, V>, 'asyncOptions' | 'hydrateValue' | 'optionsLimit'> & {
  modelValue?: V | null;
  orientation?: 'horizontal' | 'vertical';
};
const {
  options = [],
  optionDisabled,
  optionLabel,
  optionValue,
  orientation = 'horizontal',
  trackValueBy,
} = defineProps<Props>();
const resolvedOptionLabel =
  optionLabel ??
  ((option: O) => {
    if (option && typeof option === 'object' && 'label' in (option as Record<string, unknown>)) {
      return String((option as Record<string, unknown>).label ?? '');
    }
    return String(option ?? '');
  });
const resolvedOptionValue =
  optionValue ??
  ((option: O) => {
    if (option && typeof option === 'object' && 'value' in (option as Record<string, unknown>)) {
      return (option as Record<string, unknown>).value as V;
    }
    return option as unknown as V;
  });
defineSlots<{
  default?: (options: OptionsListItem<O, V>[]) => VNode;
  option?: (option: OptionsListItem<O, V>) => VNode;
}>();
const modelValue = defineModel<V | null>({ default: null });
const { optionsList, selectOption, isValueSelected } = useOptionsList<O, V>({
  options: computed(() => options),
  optionDisabled,
  optionLabel: resolvedOptionLabel,
  optionValue: resolvedOptionValue,
  trackValueBy,
  selectionMode: 'single',
  selected: modelValue,
  updateSelected(value) {
    modelValue.value = value as V | null;
  },
});
provide(SEGMENTED_CONTROL_CONTEXT, {
  isValueSelected,
  selectValue: (value: V) => {
    modelValue.value = value;
  },
});
</script>

<template>
  <div
    :class="[
      $style.root,
      orientation === 'vertical' ? $style.rootVertical : null,
    ]"
  >
    <slot v-bind="optionsList">
      <template v-for="option in optionsList" :key="option.label">
        <slot name="option" v-bind="option">
          <UiSegmentedControlOption
            :value="option.value"
            :disabled="option.disabled"
            @click="selectOption(option)"
          >
            {{ option.label }}
          </UiSegmentedControlOption>
        </slot>
      </template>
    </slot>
  </div>
</template>

<style lang="scss" module>
.root {
  display: inline-flex;
  gap: 4px;
  padding: 2px;
  border-radius: 6px;
  background: var(--ui2-shade-black-8);
}

.rootVertical {
  flex-direction: column;
  gap: 6px;
}
</style>
